import {type FC, type ReactNode, useMemo} from 'react';
import {shallowEqual, useSelector} from 'react-redux';
import {selectEventLinks} from '~/common/event/eventAttributes/reducer';
import type {EventLink} from '~/common/event/types';
import {selectPendingHostIds} from '~/common/fabric/reducers/eviteEvent/selectors';
import {pluralizeWord, prettyFormatPhoneNumber} from '~/common/utils';
import {isDraft} from '~/common/utils/event_utils';
import {
  selectCohost,
  selectGuestsOfHonor,
  selectHostIds,
  selectHostName,
  selectPhoneNumber,
} from '~/ViewInvitation/selectors/eventDetails';
import {selectStatus} from '~/ViewInvitation/selectors/eviteEvent';
import {selectGuestPreviews} from '~/ViewInvitation/selectors/whoIsComing';
import {isHost} from '~/ViewInvitation/utils';
import {Typography} from '~library/atoms/Typography';
import {CrownIcon} from '~library/svg/v2/CrownIcon';
import {LinkIcon} from '~library/svg/v2/LinkIcon';
import {PhoneIcon} from '~library/svg/v2/PhoneIcon';
import {UserIcon} from '~library/svg/v2/UserIcon';
import {HostDetailsSection} from './HostDetailsSection';

import './HostDetails.sass';

type GuestOfHonor = {name: string; email: string} | {name: string; userId: string};
type GuestPreview = {
  userId: number;
  adults: number;
  guestName: string;
  kids: number;
  rsvpComment: string | null;
  rsvpWhen: string;
  guestOfHonor?: GuestOfHonor;
};

interface HostDetailsProps {
  readonly?: boolean;
  disableContent?: boolean;
}

export const HostDetails: FC<HostDetailsProps> = ({readonly = true, disableContent = false}) => {
  const hostName: string = useSelector(selectHostName);
  const hostIds: string[] = useSelector(selectHostIds, shallowEqual);
  const cohost: {name: string; email: string} = useSelector(selectCohost, shallowEqual);
  const phoneNumber: string | undefined = useSelector(selectPhoneNumber);
  const status: string = useSelector(selectStatus);
  const pendingHostIds = useSelector(selectPendingHostIds);
  const gohs = useSelector(selectGuestsOfHonor);
  const guestPreviews = useSelector(selectGuestPreviews, shallowEqual);

  const guestsOfHonor: GuestOfHonor[] = useMemo(() => {
    if (isDraft(status) || !readonly) return gohs?.guests || []; // Include all guests of honor, even unconfirmed ones, in Create and Edit flows
    return guestPreviews
      .filter((gp: GuestPreview) => gp.guestOfHonor)
      .map((gp: GuestPreview) => ({name: gp.guestName, userId: gp.userId}));
  }, [gohs, status, guestPreviews, readonly]);

  const eventLinks: EventLink[] = useSelector(selectEventLinks, shallowEqual);

  const hostNames: ReactNode = useMemo(() => {
    if (hostIds.length > 1 && pendingHostIds.length === 0 && cohost.email) {
      return `${hostName} & ${cohost.name}`;
    }
    const hasUnsavedCohost = !pendingHostIds.length && cohost?.email && cohost?.name;
    if ((isDraft(status) || isHost()) && (pendingHostIds.length > 0 || hasUnsavedCohost)) {
      return (
        <>
          {hostName}{' '}
          <Typography as="span" variant="paragraph3" color="coin">
            +{hasUnsavedCohost ? 1 : pendingHostIds.length} (Pending)
          </Typography>
        </>
      );
    }
    return hostName;
  }, [hostIds, pendingHostIds, cohost, status, hostName]);

  return (
    <div className="view-invitation__details-module__host-details">
      {/* HOST DETAILS */}
      <HostDetailsSection
        title="Host Details"
        titleQaId="hosted-by"
        lines={[
          {
            icon: UserIcon,
            value: hostNames,
            type: 'text',
            key: 'hostname',
            qaId: 'hostname',
          },
          {
            icon: PhoneIcon,
            display_text: prettyFormatPhoneNumber(phoneNumber),
            value: `tel:1${phoneNumber}`,
            type: 'link',
            key: 'phone-number',
            qaId: 'phone-number',
            hide: !phoneNumber,
          },
        ]}
        readonly={readonly}
        disableContent={disableContent}
      />

      {/* GUEST(S) OF HONOR */}
      <HostDetailsSection
        title={`${pluralizeWord('Guest', guestsOfHonor.length)} of Honor`}
        titleQaId="guests-of-honor"
        lines={guestsOfHonor.map((guest) => ({
          icon: CrownIcon,
          key: 'email' in guest ? guest.email : guest.userId,
          value: 'email' in guest ? guest.email : guest.userId,
          display_text: guest.name,
          type: 'text',
          qaId: 'guest-of-honor',
        }))}
        readonly={readonly}
        disableContent={disableContent}
      />

      {/* LINKS */}
      <HostDetailsSection
        title="Links"
        titleQaId="additional-links"
        lines={eventLinks.map((link, index) => ({
          icon: LinkIcon,
          key: link.url,
          value: link.url,
          display_text: link.display_name || link.url,
          type: 'link',
          qaId: `event-link-${index}`,
        }))}
        readonly={readonly}
        disableContent={disableContent}
      />
    </div>
  );
};
